// TEMPORARY MOCK ADAPTER for Admin Management.
// In-memory only — state lives in this module's `admins` array and resets on hard reload.
// Mutations resolve with ~300 ms simulated latency. Errors mimic the backend envelope.
// Backed switch: VITE_USE_MOCK_API=false flips to the real HTTP adapter. See admin.adapter.ts.

import {
  AdminApiError,
  ROLE_FILTER_ROOT,
  type Admin,
  type AdminApi,
  type AdminSortKey,
  type InviteAdminInput,
  type ListAdminsParams,
  type ListAdminsResult,
  type RootTransferInput,
  type RootTransferResult,
  type SortDirection,
  type UpdateAdminInput,
} from '../types/admin.types';
import { normalizePhone } from '@/shared/utils/phone';

const LATENCY_MS = 300;
// Email format: TLD >=2 ASCII letters — mirror the real backend / FE form rule (aligns to the spec email rule).
const EMAIL_REGEX = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
// PRD 15 §4.2: exactly 11 digits, '010'-prefixed (tested on digits-only value).
const PHONE_REGEX = /^010\d{8}$/;
const NAME_MIN = 2;
const NAME_MAX = 50;

const now = () => new Date().toISOString();
const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value));
const delay = (ms: number = LATENCY_MS): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

// Seed mirrors prototype `MockData.adminManagers` (mock-data.js §4-H) — same 6 rows,
// normalized to the canonical English status enum and ISO datetimes.
// v9 (DEC-DB-PK-BIGINT-001): ids are BIGINT UNSIGNED → number on the wire.
let admins: Admin[] = [
  {
    id: 1,
    email: 'hw.kang@carelink.co.kr',
    name: '강현우',
    phone: '010-1234-5678',
    institution: '울산대학교병원',
    department: '정신건강의학과',
    roles: ['OPERATION'],
    isRoot: false,
    status: 'ACTIVE',
    joinedAt: '2024-01-15T00:00:00.000Z',
    lastLoginAt: '2024-10-28T09:15:00.000Z',
    passwordChangedAt: '2026-02-10T00:00:00.000Z',
  },
  {
    id: 2,
    email: 'staff@carelink.co.kr',
    name: '이담당',
    phone: '010-2345-6789',
    institution: '울산대학교병원',
    department: '임상시험센터',
    roles: ['OPERATION', 'CONTENT'],
    isRoot: false,
    status: 'ACTIVE',
    joinedAt: '2024-02-20T00:00:00.000Z',
    lastLoginAt: '2024-10-27T14:22:00.000Z',
    passwordChangedAt: '2026-03-05T00:00:00.000Z',
  },
  {
    id: 3,
    email: 'manager@carelink.co.kr',
    name: '박관리',
    phone: '010-3456-7890',
    institution: '울산대학교병원',
    department: '신경과',
    roles: [],
    isRoot: true,
    status: 'ACTIVE',
    joinedAt: '2024-03-10T00:00:00.000Z',
    lastLoginAt: '2024-10-28T10:03:00.000Z',
    passwordChangedAt: '2026-01-22T00:00:00.000Z',
  },
  {
    id: 4,
    email: 'invited@carelink.co.kr',
    name: '최초대',
    phone: null,
    institution: '울산대학교병원',
    department: '간호부',
    roles: ['CONTENT'],
    isRoot: false,
    status: 'PENDING',
    joinedAt: null,
    lastLoginAt: null,
    passwordChangedAt: null,
  },
  {
    id: 5,
    email: 'withdrawn@carelink.co.kr',
    name: '김탈퇴',
    phone: '010-5678-9012',
    institution: '울산대학교병원',
    department: '재활의학과',
    roles: ['SYSTEM'],
    isRoot: false,
    status: 'WITHDRAWN',
    joinedAt: null,
    lastLoginAt: null,
    passwordChangedAt: null,
  },
  {
    id: 6,
    email: 'onleave@carelink.co.kr',
    name: '박휴직',
    phone: '010-6789-0123',
    institution: '울산대학교병원',
    department: '가정의학과',
    roles: ['OPERATION'],
    isRoot: false,
    status: 'INACTIVE',
    joinedAt: '2024-05-20T00:00:00.000Z',
    lastLoginAt: '2024-08-15T10:30:00.000Z',
    passwordChangedAt: '2024-06-01T00:00:00.000Z',
    note: '육아휴직 중',
    inactiveSince: '2024-08-16T00:00:00.000Z',
    inactiveHistory: [
      {
        reason: '단기 출장',
        from: '2024-04-01T00:00:00.000Z',
        to: '2024-04-30T00:00:00.000Z',
      },
    ],
  },
];

let nextId = Math.max(...admins.map((a) => a.id)) + 1;

// Internal counter for the prototype's 3-strike lock on root transfer (PRD §3.5.4).
let transferPwAttempts = 0;
let transferLockedUntil = 0;

function findById(id: number): Admin {
  const a = admins.find((x) => x.id === id);
  if (!a) throw new AdminApiError('NOT_FOUND', '해당 관리자를 찾을 수 없습니다.');
  return a;
}

function validateName(name: string): void {
  const trimmed = name.trim();
  if (trimmed.length < NAME_MIN || trimmed.length > NAME_MAX) {
    throw new AdminApiError('NAME_INVALID', '이름을 입력해주세요. (2~50자)', {
      fields: { name: '이름을 입력해주세요. (2~50자)' },
    });
  }
}

function validateEmail(email: string): void {
  if (!EMAIL_REGEX.test(email)) {
    throw new AdminApiError('EMAIL_INVALID', '올바른 이메일 형식을 입력해주세요.', {
      fields: { email: '올바른 이메일 형식을 입력해주세요.' },
    });
  }
}

function validatePhone(phone: string | null): void {
  const digits = normalizePhone(phone);
  if (digits && !PHONE_REGEX.test(digits)) {
    const message =
      '올바른 휴대폰 번호 형식을 입력해주세요. (010으로 시작하는 11자리 숫자)';
    throw new AdminApiError('PHONE_INVALID', message, {
      fields: { phone: message },
    });
  }
}

function ensureUniqueEmail(email: string, excludeId?: number): void {
  const taken = admins.some(
    (a) => a.email.toLowerCase() === email.toLowerCase() && a.id !== excludeId,
  );
  if (taken) {
    throw new AdminApiError('EMAIL_DUPLICATE', '이미 등록된 이메일입니다.', {
      fields: { email: '이미 등록된 이메일입니다.' },
    });
  }
}

function validateRoles(roles: string[]): void {
  if (!Array.isArray(roles) || roles.length === 0) {
    // SHCT-523 — PRD 15 v2.4 §3.2·§3.3 조건분기 wording. The old "1개 이상" phrasing
    // described the abolished multi-Role rule.
    throw new AdminApiError('ROLE_REQUIRED', 'Role을 선택해주세요.', {
      fields: { roles: 'Role을 선택해주세요.' },
    });
  }
}

function compareAdmins(a: Admin, b: Admin, key: AdminSortKey, dir: SortDirection): number {
  const sign = dir === 'asc' ? 1 : -1;
  if (key === 'id') return (a.id - b.id) * sign;
  if (key === 'name') return a.name.localeCompare(b.name) * sign;
  if (key === 'role') {
    // Root pinned to the top of `asc` (matching prototype's '!ROOT' trick).
    const va = a.isRoot ? '!ROOT' : a.roles[0] ?? 'zz';
    const vb = b.isRoot ? '!ROOT' : b.roles[0] ?? 'zz';
    return va.localeCompare(vb) * sign;
  }
  if (key === 'status') {
    const order: Record<Admin['status'], number> = {
      ACTIVE: 1,
      INACTIVE: 2,
      PENDING: 3,
      WITHDRAWN: 4,
    };
    return (order[a.status] - order[b.status]) * sign;
  }
  // lastLoginAt: null always sinks regardless of direction.
  const va = a.lastLoginAt;
  const vb = b.lastLoginAt;
  if (va === null && vb !== null) return 1;
  if (vb === null && va !== null) return -1;
  if (va === null && vb === null) return 0;
  return (va! < vb! ? -1 : va! > vb! ? 1 : 0) * sign;
}

export const adminMockApi: AdminApi = {
  async listAdmins(params: ListAdminsParams): Promise<ListAdminsResult> {
    await delay();

    const { role, status, q } = params;
    const page = Math.max(1, params.page ?? 1);
    const perPage = Math.max(1, params.perPage ?? 20);
    const sortBy: AdminSortKey = params.sortBy ?? 'id';
    const sortDir: SortDirection = params.sortDir ?? 'desc';

    let filtered = [...admins];

    if (role === ROLE_FILTER_ROOT) {
      filtered = filtered.filter((a) => a.isRoot);
    } else if (role) {
      filtered = filtered.filter((a) => !a.isRoot && a.roles.includes(role));
    }

    if (status) filtered = filtered.filter((a) => a.status === status);

    if (q) {
      const needle = q.trim().toLowerCase();
      if (needle) {
        filtered = filtered.filter(
          (a) =>
            a.name.toLowerCase().includes(needle) || a.email.toLowerCase().includes(needle),
        );
      }
    }

    filtered.sort((a, b) => compareAdmins(a, b, sortBy, sortDir));

    const total = filtered.length;
    const start = (page - 1) * perPage;
    const items = filtered.slice(start, start + perPage);

    return clone({ items, total, page, perPage });
  },

  async inviteAdmin(input: InviteAdminInput): Promise<Admin> {
    await delay();
    validateName(input.name);
    validateEmail(input.email);
    validatePhone(input.phone);
    validateRoles(input.roles);
    ensureUniqueEmail(input.email);

    const created: Admin = {
      id: nextId++,
      email: input.email,
      name: input.name.trim(),
      phone: input.phone,
      institution: '울산대학교병원',
      department: null,
      roles: [...input.roles],
      isRoot: false,
      status: 'PENDING',
      joinedAt: null,
      lastLoginAt: null,
      passwordChangedAt: null,
    };
    admins = [created, ...admins];
    return clone(created);
  },

  async resendInvitation(id: number): Promise<void> {
    await delay();
    const target = findById(id);
    if (target.status !== 'PENDING') {
      throw new AdminApiError(
        'INVALID_TARGET_STATUS',
        '미가입 상태의 관리자에게만 재발송할 수 있습니다.',
      );
    }
  },

  async updateAdmin(id: number, input: UpdateAdminInput): Promise<Admin> {
    await delay();
    const target = findById(id);
    if (target.isRoot) {
      throw new AdminApiError(
        'ROOT_NOT_EDITABLE',
        '관리자 계정은 편집할 수 없습니다. 이양 후 편집 가능합니다.',
      );
    }
    // Withdrawn → anything: rejected (permanent per PRD §5).
    if (
      target.status === 'WITHDRAWN' &&
      input.status !== undefined &&
      input.status !== 'WITHDRAWN'
    ) {
      throw new AdminApiError(
        'INVALID_TARGET_STATUS',
        '탈퇴한 계정은 상태를 변경할 수 없습니다.',
      );
    }
    if (input.name !== undefined) {
      validateName(input.name);
      target.name = input.name.trim();
    }
    if (input.phone !== undefined) {
      validatePhone(input.phone);
      target.phone = input.phone;
    }
    if (input.roles !== undefined) {
      validateRoles(input.roles);
      target.roles = [...input.roles];
    }

    // Note + status side-effects (PRD update — note is the inactive reason).
    const prevStatus = target.status;
    const prevNote = target.note ?? '';
    const incomingNote = input.note;
    const nextStatus = input.status ?? prevStatus;

    if (input.status !== undefined) {
      target.status = input.status;
    }

    if (nextStatus === 'ACTIVE') {
      // Inactive → Active: archive prior note into inactiveHistory, then clear.
      if (prevStatus === 'INACTIVE') {
        if (!Array.isArray(target.inactiveHistory)) target.inactiveHistory = [];
        target.inactiveHistory.push({
          reason: prevNote.trim() || '(사유 미기재)',
          from: target.inactiveSince ?? '-',
          to: now(),
        });
      }
      target.note = null;
      target.inactiveSince = null;
    } else if (nextStatus === 'INACTIVE') {
      target.note = incomingNote ?? prevNote;
      if (prevStatus !== 'INACTIVE') target.inactiveSince = now();
    } else {
      // Unregistered / Withdrawn: respect incoming note as-is (Withdraw flow sends final note).
      if (incomingNote !== undefined) target.note = incomingNote;
    }

    return clone(target);
  },

  async resetPassword(id: number): Promise<void> {
    await delay();
    const target = findById(id);
    if (target.isRoot) {
      throw new AdminApiError(
        'INVALID_TARGET_STATUS',
        'Root 계정은 이양 후 비밀번호 초기화가 가능합니다.',
      );
    }
    if (target.status !== 'ACTIVE') {
      throw new AdminApiError(
        'INVALID_TARGET_STATUS',
        '활성 상태의 관리자에게만 비밀번호 초기화가 가능합니다.',
      );
    }
  },

  async listRootTransferCandidates(): Promise<Admin[]> {
    await delay();
    return clone(admins.filter((a) => a.status === 'ACTIVE' && !a.isRoot));
  },

  async rootTransfer(input: RootTransferInput): Promise<RootTransferResult> {
    await delay();

    if (Date.now() < transferLockedUntil) {
      const retryAfterSeconds = Math.ceil((transferLockedUntil - Date.now()) / 1000);
      throw new AdminApiError(
        'LOCKED_5MIN',
        '비밀번호 3회 오입력으로 이양 기능이 5분간 잠금되었습니다.',
        { meta: { retryAfterSeconds } },
      );
    }

    const candidates = admins.filter((a) => a.status === 'ACTIVE' && !a.isRoot);
    if (candidates.length === 0) {
      throw new AdminApiError(
        'NO_CANDIDATES',
        '이양 가능한 활성 어드민이 없습니다. 먼저 어드민을 초대·활성화해주세요.',
      );
    }

    const target = candidates.find((a) => a.id === input.targetAdminId);
    if (!target) {
      throw new AdminApiError(
        'TARGET_INVALID',
        '선택한 이양 대상이 유효하지 않습니다. (활성 + 비-Root 만 가능)',
      );
    }

    // Prototype convention from admin-managers.html: 'wrongpass' simulates failure.
    if (input.password === 'wrongpass') {
      transferPwAttempts += 1;
      if (transferPwAttempts >= 3) {
        transferLockedUntil = Date.now() + 5 * 60 * 1000;
        transferPwAttempts = 0;
        throw new AdminApiError(
          'LOCKED_5MIN',
          '비밀번호 3회 오입력 — 5분간 이양 기능이 잠금되었습니다.',
          { meta: { retryAfterSeconds: 5 * 60 } },
        );
      }
      throw new AdminApiError(
        'PASSWORD_INVALID',
        `비밀번호가 일치하지 않습니다. (${transferPwAttempts}/3회 실패)`,
        { meta: { attemptsLeft: 3 - transferPwAttempts } },
      );
    }

    // Atomic swap (single-transaction simulation).
    const currentRoot = admins.find((a) => a.isRoot);
    if (!currentRoot) {
      throw new AdminApiError('TRANSACTION_FAILED', '현재 Root 계정을 찾을 수 없습니다.');
    }
    currentRoot.isRoot = false;
    currentRoot.roles = [...input.keepRoles];
    target.isRoot = true;
    target.roles = [];

    transferPwAttempts = 0;
    return clone({ newRoot: target, previousRoot: currentRoot, forcedLogout: true });
  },
};
